import { WORKDAY_INTEGRATION_TYPE } from './constants';
import type { WorkdayPosition } from './types';

/**
 * The one condition behind every surface: with the flow on and no position
 * ready to hire into, "Add to Workday" would fail at the vendor.
 *
 * Reads `staffing_entity_id`, not `status`. That is Kombo's own id for the
 * position and it only appears once Kombo's sync has ingested it — which is
 * the moment a hire can actually go through. `status` can say `ready` a beat
 * before that, and the employee form needs the id itself.
 *
 * `failed` and absent are both just "no position yet". Shared so the surfaces
 * cannot drift apart.
 */
export const needsWorkdayPosition = ({
  isEnabled,
  integrationType,
  position,
}: {
  isEnabled?: boolean;
  integrationType?: string;
  /** `workday_position` off the application. Absent — not null — when none. */
  position?: WorkdayPosition | null;
}) =>
  !!isEnabled &&
  integrationType === WORKDAY_INTEGRATION_TYPE &&
  !position?.staffing_entity_id;

interface IntegrationLike {
  type?: string;
  name?: string;
  status?: string;
}

interface VisibilityArgs {
  /** The whole HRIS surface is hired-stage only. */
  isHiredStage?: boolean;
  /** `useWorkdayPositionGate().isEnabled` — flag AND Workday connected. */
  isWorkdayPositionEnabled?: boolean;
  /** Live position state (status response if there is one, else the payload). */
  position?: WorkdayPosition | null;
  /** `pushed_to_hris` from the application. */
  pushedTypes?: string[];
  /** Active HRIS integrations for the account. */
  hrisIntegrations?: IntegrationLike[];
}

/**
 * Workday's generic "Add to" CTA is replaced by the position card until the
 * position is ready.
 */
export const getWorkdayPositionVisibility = ({
  isHiredStage = false,
  isWorkdayPositionEnabled = false,
  position,
  pushedTypes = [],
  hrisIntegrations = [],
}: VisibilityArgs) => {
  const isWorkdayPushed = pushedTypes.includes(WORKDAY_INTEGRATION_TYPE);

  // Flag off means nothing at all, even for an application that already has a
  // position: the flow is unreachable, so a status row would only describe
  // something the recruiter has no way to act on.
  const showCard = isWorkdayPositionEnabled && isHiredStage && !isWorkdayPushed;

  const hrisIntegrationsForPush = hrisIntegrations.filter(
    (integration) =>
      !needsWorkdayPosition({
        isEnabled: isWorkdayPositionEnabled,
        integrationType: integration?.type,
        position,
      })
  );

  return {
    showCard,
    hrisIntegrationsForPush,
  };
};
